'use client';

import { useGlobalState } from '@/layers/presentation/context/GlobalStateContext';
import { useTranslation } from '@/layers/presentation/i18n/client';
import { UserType } from '@/types/modelEnums';
import { cn } from '@/utils/cn';
import {
    Button,
    Link,
    NavbarBrand,
    NavbarContent,
    NavbarItem,
    NavbarMenu,
    NavbarMenuItem,
    NavbarMenuToggle,
    Navbar as NextUINavbar,
} from '@nextui-org/react';
import { LogIn, LogOut, User } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import Logo from './Logo';
import Menu from './Menu';

type NavbarVariant = 'default' | 'transparent' | 'dark';

interface NavbarProps {
    variant?: NavbarVariant;
}

const Navbar: React.FC<NavbarProps> = ({ variant = 'default' }) => {
    const { t } = useTranslation();
    const { user } = useGlobalState();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const pathname = usePathname();

    // Determine the appropriate auth path based on the current route
    const getAuthPath = () => {
        if (pathname.startsWith('/patient/') || pathname.startsWith('/about/patients')) {
            return '/users/auth/patient';
        } else if (pathname.startsWith('/provider/') || pathname.startsWith('/about/providers')) {
            return '/users/auth/provider';
        }
        // Default to provider auth path if no specific context
        return '/users/auth/provider';
    };

    const variantStyles = {
        default:
            'bg-white/90 backdrop-blur-[50px] !backdrop-grayscale-[0.5] border-b border-primary',
        transparent: 'bg-transparent',
        dark: 'bg-primary/90 backdrop-blur-[50px]',
    };

    const textStyles = {
        default: 'text-primary',
        transparent: 'text-primary',
        dark: 'text-white',
    };

    const isActive = (href: string) => {
        // For first-level paths like /provider, /sponsor - require exact match
        if (href.split('/').filter(Boolean).length === 1) {
            return pathname === href;
        }
        // For deeper paths, allow matching with subpaths
        return pathname === href || pathname.startsWith(`${href}/`);
    };

    const menuItems = [
        ...((user?.type === UserType.PROVIDER && [
            {
                href: '/provider',
                label: t('components:navbar.provider.main'),
                isActive: isActive('/provider'),
            },
            {
                href: '/provider/programs',
                label: t('components:navbar.provider.searchPrograms'),
                isActive: isActive('/provider/programs'),
            },
            {
                href: '/provider/patients',
                label: t('components:navbar.provider.patients'),
                isActive: isActive('/provider/patients'),
            },
        ]) ||
            []),
        ...((user?.type === UserType.SPONSOR && [
            {
                href: '/sponsor',
                label: t('components:navbar.sponsor.main'),
                isActive: isActive('/sponsor'),
            },
            {
                href: '/sponsor/programs',
                label: t('components:navbar.sponsor.myPrograms'),
                isActive: isActive('/sponsor/programs'),
            },
            {
                href: '/sponsor/programRequests',
                label: t('components:navbar.sponsor.programRequests'),
                isActive: isActive('/sponsor/programRequests'),
            },
            {
                href: '/sponsor/reports',
                label: t('components:navbar.sponsor.reports'),
                isActive: isActive('/sponsor/reports'),
            },
        ]) ||
            []),
        ...((user?.type === UserType.PATIENT && [
            {
                href: '/patient',
                label: t('components:navbar.patient.main'),
                isActive: isActive('/patient'),
            },
            {
                href: '/patient/programs',
                label: t('components:navbar.patient.myPrograms'),
                isActive: isActive('/patient/programs'),
            },
        ]) ||
            []),
        ...((!user && [
            {
                href: '/about/patients',
                label: t('components:navbar.about.forPatients'),
                isActive: isActive('/about/patients'),
            },
            {
                href: '/about/providers',
                label: t('components:navbar.about.forProviders'),
                isActive: isActive('/about/providers'),
            },
            {
                href: '/about/sponsors',
                label: t('components:navbar.about.forSponsors'),
                isActive: isActive('/about/sponsors'),
            },
            {
                href: '/about/contact-us',
                label: t('components:navbar.about.contactUs'),
                isActive: isActive('/about/contact-us'),
            },
            {
                href: '/about/faq',
                label: t('components:navbar.about.faq'),
                isActive: isActive('/about/faq'),
            },
        ]) ||
            []),
        {
            href: '/blog',
            label: t('components:navbar.blog'),
            isActive: isActive('/blog'),
        },
    ];

    return (
        <NextUINavbar
            maxWidth="full"
            className={cn(
                'p-3 fixed top-0 left-0 right-0 z-50 px-0 sm:px-6',
                variantStyles[variant],
            )}
            isMenuOpen={isMenuOpen}
            onMenuOpenChange={setIsMenuOpen}
        >
            <NavbarContent className="gap-4">
                <NavbarBrand>
                    <Logo className="flex" variant={variant} />
                </NavbarBrand>
            </NavbarContent>

            <NavbarContent className="hidden sm:flex" justify="center">
                <Menu menuItems={menuItems} className={textStyles[variant]} />
            </NavbarContent>

            <NavbarContent justify="end">
                <NavbarItem key="login" className="hidden sm:flex">
                    {user ? (
                        <div className="flex flex-row gap-4 items-center">
                            <Button
                                as={Link}
                                href="/users/profile"
                                variant="bordered"
                                startContent={<User size={18} />}
                                className={cn('border-1', textStyles[variant])}
                            >
                                {t('components:navbar.myProfile')}
                            </Button>
                            <Button
                                as={Link}
                                href="/users/auth?logout=true"
                                variant="bordered"
                                startContent={<LogOut size={18} />}
                                className={cn('border-1', textStyles[variant])}
                            >
                                Logout
                            </Button>
                        </div>
                    ) : (
                        <Button
                            as={Link}
                            variant="bordered"
                            startContent={<LogIn size={18} />}
                            className={cn('border-1', variant === 'dark' && 'text-white')}
                            href={getAuthPath()}
                        >
                            Login or Sign Up
                        </Button>
                    )}
                </NavbarItem>

                <NavbarMenuToggle
                    aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                    className={cn('sm:hidden', variant === 'dark' && 'text-white')}
                />
            </NavbarContent>

            <NavbarMenu className="pt-6">
                {menuItems.map((item, index) => (
                    <NavbarMenuItem key={`${item.href}-${index}`}>
                        <Link
                            href={item.href}
                            className={cn(
                                'w-full text-lg py-2',
                                isActive(item.href) && 'font-semibold text-primary',
                            )}
                            onClick={() => setIsMenuOpen(false)}
                        >
                            {item.label}
                        </Link>
                    </NavbarMenuItem>
                ))}

                <NavbarMenuItem className="mt-6">
                    {user ? (
                        <>
                            <Link
                                href="/users/profile"
                                className={cn(
                                    'w-full text-lg py-2',
                                    isActive('/users/profile') && 'font-semibold text-primary',
                                )}
                                onClick={() => setIsMenuOpen(false)}
                            >
                                <div className="flex items-center gap-2">
                                    <User size={18} />
                                    {t('components:navbar.myProfile')}
                                </div>
                            </Link>
                            <Link
                                href="/users/auth?logout=true"
                                className="w-full text-lg py-2"
                                onClick={() => setIsMenuOpen(false)}
                            >
                                <div className="flex items-center gap-2">
                                    <LogOut size={18} />
                                    Logout
                                </div>
                            </Link>
                        </>
                    ) : (
                        <Link
                            href={getAuthPath()}
                            className={cn(
                                'w-full text-lg py-2',
                                isActive('/users/auth') && 'font-semibold text-primary',
                            )}
                            onClick={() => setIsMenuOpen(false)}
                        >
                            <div className="flex items-center gap-2">
                                <LogIn size={18} />
                                Login or Create an Account
                            </div>
                        </Link>
                    )}
                </NavbarMenuItem>
            </NavbarMenu>
        </NextUINavbar>
    );
};

export default Navbar;
